import { COMPONENT_STATUS_BY_WEAR_STATUS, SINGLE_UNIT_COUNT, WEAR_SECTIONS } from '../../constants';
import { componentSections } from '../../mockData';
import useTranslation from '../useTranslation';
import { useEffect } from 'react';
import groupBy from 'utils/groupBy';

import useDroneWear from 'hooks/useDroneWear';

import type { ComponentRow, LabeledSection, PartUnitWear, Props, UseData } from './types';

const useData = ({ drone }: Props): UseData => {
    const { NO_INTERVAL, PART_LABELS, SECTION_TITLES, WEAR_DETAIL } = useTranslation();
    const { fetch, isLoading, units } = useDroneWear({ drone });

    useEffect(() => {
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drone.id]);

    const getRow = ({
        hasManyUnits,
        unit
    }: {
        hasManyUnits: boolean;
        unit: PartUnitWear;
    }): ComponentRow => {
        const { intervalUnit, partType, percentRemaining, remaining, status, unitIndex } = unit;

        const partLabel = PART_LABELS[partType] ?? partType;

        const detail =
            percentRemaining === null || remaining === null
                ? NO_INTERVAL
                : WEAR_DETAIL({
                      intervalUnit,
                      percent: percentRemaining,
                      remaining: Math.round(remaining)
                  });

        return {
            detail,
            label: hasManyUnits ? `${partLabel} ${unitIndex}` : partLabel,
            percentRemaining: percentRemaining ?? undefined,
            status: COMPONENT_STATUS_BY_WEAR_STATUS[status]
        };
    };

    const unitsByPartType = groupBy(units, ({ partType }) => partType);

    const wearSections = WEAR_SECTIONS.reduce<LabeledSection[]>(
        (sections, { partType, titleKey }) => {
            const partUnits = unitsByPartType.get(partType) ?? [];

            if (!partUnits.length) {
                return sections;
            }

            const hasManyUnits = partUnits.length > SINGLE_UNIT_COUNT;

            return [
                ...sections,
                {
                    rows: partUnits.map(unit => getRow({ hasManyUnits, unit })),
                    title: SECTION_TITLES[titleKey],
                    titleKey
                }
            ];
        },
        []
    );

    const mockedSections = componentSections.map(({ rows, titleKey }) => ({
        rows,
        title: SECTION_TITLES[titleKey],
        titleKey
    }));

    return { isLoading, sections: [...wearSections, ...mockedSections] };
};

export default useData;
